import type { CarePlanEvidence } from '@eluve/api-contract';

const EVIDENCE_CONTEXT_CHARACTERS = 180;

type EvidenceSpan = NonNullable<CarePlanEvidence['span']>;

export type EvidenceLocation = {
  before: string;
  highlight: string;
  after: string;
};

type LocateEvidenceInput = Pick<CarePlanEvidence, 'quote' | 'span'> & {
  transcript: string;
};

const quoteMatchesSpan = (
  transcript: string,
  quote: string,
  span: EvidenceSpan | null,
): span is EvidenceSpan => {
  if (
    span === null ||
    !Number.isInteger(span.start) ||
    !Number.isInteger(span.end) ||
    span.start < 0 ||
    span.end < span.start ||
    span.end > transcript.length
  ) {
    return false;
  }

  return transcript.slice(span.start, span.end) === quote;
};

const findUniqueQuoteStart = (
  transcript: string,
  quote: string,
): number | null => {
  const firstMatch = transcript.indexOf(quote);

  if (firstMatch === -1 || transcript.includes(quote, firstMatch + 1)) {
    return null;
  }

  return firstMatch;
};

const locationAt = (
  transcript: string,
  quote: string,
  start: number,
): EvidenceLocation => {
  const end = start + quote.length;
  const contextStart = Math.max(start - EVIDENCE_CONTEXT_CHARACTERS, 0);
  const contextEnd = Math.min(
    end + EVIDENCE_CONTEXT_CHARACTERS,
    transcript.length,
  );

  return {
    before: transcript.slice(contextStart, start),
    highlight: quote,
    after: transcript.slice(end, contextEnd),
  };
};

export const locateEvidence = ({
  transcript,
  quote,
  span,
}: LocateEvidenceInput): EvidenceLocation | null => {
  if (quote.length === 0) {
    return null;
  }

  // A verified stored offset is the most trustworthy match.
  if (quoteMatchesSpan(transcript, quote, span)) {
    return locationAt(transcript, quote, span.start);
  }

  // Otherwise — stale or missing offset — a quote that occurs exactly once is
  // still an unambiguous, safe match.
  const fallbackStart = findUniqueQuoteStart(transcript, quote);

  return fallbackStart === null
    ? null
    : locationAt(transcript, quote, fallbackStart);
};
